import { apiFetch } from '../../../lib/api';
import { getRoles } from '../rbac';

/**
 * Fetches the paginated list of users for the admin dashboard.
 * @param {Object} params - Query parameters (skip, limit, search, role, is_active)
 * @param {string} token - Access token from AuthContext
 */
export const getAdminUsers = async (params = {}, token) => {
    const searchParams = new URLSearchParams();
    
    // Default pagination params as per API specs
    searchParams.append('skip', params.skip !== undefined ? params.skip : 0);
    searchParams.append('limit', params.limit !== undefined ? params.limit : 50);
    
    if (params.search) {
        searchParams.append('search', params.search);
    }
    
    // Role filter
    if (params.role_id && params.role_id !== 'all') {
        searchParams.append('role_id', params.role_id);
    }
    
    // active status filter
    if (params.is_active !== undefined && params.is_active !== null) {
        searchParams.append('is_active', params.is_active);
    }
    
    // verified status filter
    if (params.is_verified !== undefined && params.is_verified !== null) {
        searchParams.append('is_verified', params.is_verified);
    }

    if (params.sort) {
        searchParams.append('sort', params.sort);
    }

    const queryString = searchParams.toString();
    
    // Corrected endpoint path based on swagger specification
    const endpoint = `/api/v1/auth/admin/users?${queryString}`;

    return apiFetch(endpoint, {
        method: 'GET',
        // apiFetch typically handles token internally, but we explicitely pass the context token as requested
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Admin Update User (Generic)
 * Supports updating role, is_active, email, and personal info.
 * @param {string} userId - Target user ID
 * @param {Object} updateData - Data to update (e.g. { role: 'admin', is_active: true })
 * @param {string} token - Access token
 */
export const updateAdminUser = async (userId, updateData, token) => {
    const endpoint = `/api/v1/auth/admin/users/${userId}`;
    return apiFetch(endpoint, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(updateData),
    });
};

/**
 * Updates a user's role using the generic update function.
 */
export const updateUserRole = async (userId, newRole, token) => {
    return updateAdminUser(userId, { role: newRole }, token);
};

/**
 * Activates or deactivates a user account using the generic update function or direct action endpoint.
 */
export const setUserActiveStatus = async (userId, action, token) => {
    const actionPath = (action === true || action === 'activate') ? 'activate' : 'deactivate';
    const endpoint = `/api/v1/auth/admin/users/${userId}/${actionPath}`;
    return apiFetch(endpoint, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};
/**
 * Admin Delete User
 * @param {string} userId - Target user ID
 * @param {string} token - Access token
 */
export const deleteAdminUser = async (userId, token) => {
    const endpoint = `/api/v1/auth/admin/users/${userId}`;
    return apiFetch(endpoint, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Admin Get User Delete Blockers
 * @param {string} userId - Target user ID
 * @param {string} token - Access token
 */
export const getAdminUserDeleteBlockers = async (userId, token) => {
    const endpoint = `/api/v1/auth/admin/users/${userId}/delete-blockers`;
    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Admin Delete User With Lead Reassignment
 * @param {string} userId - Target user ID
 * @param {Object} payload - Reassignment payload containing { reassignments: [{school_id, lead_school_staff_id}] }
 * @param {string} token - Access token
 */
export const deleteAdminUserWithReassignment = async (userId, payload, token) => {
    const endpoint = `/api/v1/auth/admin/users/${userId}/delete-with-lead-reassignment`;
    return apiFetch(endpoint, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(payload),
    });
};

/**
 * Admin Verify User Email
 * @param {string} userId - Target user ID
 * @param {string} token - Access token
 */
export const verifyAdminUserEmail = async (userId, token) => {
    const endpoint = `/api/v1/auth/admin/users/${userId}/verify`;
    return apiFetch(endpoint, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Admin Create User
 * @param {Object} userData - User creation data
 * @param {string} token - Access token
 */
export const createAdminUser = async (userData, token) => {
    const endpoint = `/api/v1/auth/admin/users`;
    return apiFetch(endpoint, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(userData),
    });
};

/**
 * Admin: Get student profile settings.
 * @param {string} userId - Target user UUID
 * @param {string} [token] - Optional access token
 */
export const getAdminStudentProfile = async (userId, token) => {
    const endpoint = `/api/v1/auth/admin/users/${userId}/student`;
    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Admin: Update student profile settings.
 * @param {string} userId - Target user UUID
 * @param {Object} profileData - Profile details to update
 * @param {string} [token] - Optional access token
 */
export const updateAdminStudentProfile = async (userId, profileData, token) => {
    const endpoint = `/api/v1/auth/admin/users/${userId}/student`;
    return apiFetch(endpoint, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(profileData),
    });
};

/**
 * Admin: Get parent profile settings.
 * @param {string} userId - Target user UUID
 * @param {string} [token] - Optional access token
 */
export const getAdminParentProfile = async (userId, token) => {
    const endpoint = `/api/v1/auth/admin/users/${userId}/parent`;
    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Admin: Update parent profile settings.
 * @param {string} userId - Target user UUID
 * @param {Object} profileData - Profile details to update
 * @param {string} [token] - Optional access token
 */
export const updateAdminParentProfile = async (userId, profileData, token) => {
    const endpoint = `/api/v1/auth/admin/users/${userId}/parent`;
    return apiFetch(endpoint, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(profileData),
    });
};

/**
 * Get current user's parent profile settings.
 * @param {string} [token] - Optional access token
 */
export const getMyParent = async (token) => {
    const endpoint = '/api/v1/users/me/parent';
    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Update current user's parent profile settings.
 * @param {Object} parentData - Parent details to update
 * @param {string} [token] - Optional access token
 */
export const updateMyParent = async (parentData, token) => {
    const endpoint = '/api/v1/users/me/parent';
    return apiFetch(endpoint, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(parentData),
    });
};

/**
 * Get current user's student profile settings.
 * @param {string} [token] - Optional access token
 */
export const getMyStudent = async (token) => {
    const endpoint = '/api/v1/users/me/student';
    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Update current user's student profile settings.
 * @param {Object} studentData - Student details to update
 * @param {string} [token] - Optional access token
 */
export const updateMyStudent = async (studentData, token) => {
    const endpoint = '/api/v1/users/me/student';
    return apiFetch(endpoint, {
        method: 'PATCH',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(studentData),
    });
};

/**
 * Admin: list family relations for a parent user.
 */
export const listAdminParentFamily = async (parentUserId, token, params = {}) => {
    const searchParams = new URLSearchParams();
    searchParams.append('limit', params.limit !== undefined ? params.limit : 100);
    searchParams.append('skip', params.skip !== undefined ? params.skip : 0);
    if (params.is_active !== undefined && params.is_active !== null) {
        searchParams.append('is_active', params.is_active);
    }
    const endpoint = `/api/v1/auth/admin/users/${parentUserId}/myfamily?${searchParams}`;
    return apiFetch(endpoint, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Admin: link a student to a parent user.
 */
export const createAdminParentFamilyRelation = async (parentUserId, data, token) => {
    const endpoint = `/api/v1/auth/admin/users/${parentUserId}/myfamily`;
    return apiFetch(endpoint, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: JSON.stringify(data),
    });
};

/**
 * Admin: remove a parent–student family relation.
 */
export const deleteAdminParentFamilyRelation = async (parentUserId, relationId, token) => {
    const endpoint = `/api/v1/auth/admin/users/${parentUserId}/myfamily/${relationId}`;
    return apiFetch(endpoint, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
};

/**
 * Find the active parent link for a student (scans parent accounts).
 */
export const findStudentParentLink = async (studentId, token) => {
    let parentRoleId = null;
    try {
        const rolesData = await getRoles({ limit: 100 });
        const roles = Array.isArray(rolesData) ? rolesData : (rolesData?.items || []);
        const parentRole = roles.find(r => r.name.toLowerCase() === 'parent');
        parentRoleId = parentRole?.id;
    } catch (err) {
        console.warn('Could not fetch roles for findStudentParentLink', err);
    }
    
    if (!parentRoleId) return null;

    const data = await getAdminUsers({ role_id: parentRoleId, limit: 100, skip: 0 }, token);
    const parents = data?.items || [];
    for (const parent of parents) {
        try {
            const relations = await listAdminParentFamily(parent.id, token, { is_active: true, limit: 100 });
            const items = relations?.items || [];
            const match = items.find((r) => r.student_id === studentId);
            if (match) {
                return { parentUserId: parent.id, relationId: match.id };
            }
        } catch {
            // try next parent
        }
    }
    return null;
};

export { getAdminUserSessions, revokeAdminUserSessions } from './sessions';



